/**
 * User-action breadcrumb buffer for bug reports.
 *
 * Records lightweight "what did they do" events — clicks, route changes,
 * form submits, modal opens, manually-flagged feature events — into a
 * rolling buffer that the SupportModal attaches to a bug report.
 *
 * Privacy rule, hard: never capture input *values*, only the fact that
 * something happened. The label of a clicked element is enough; what
 * the user typed is not our business.
 */

export type BreadcrumbType =
  | 'click'
  | 'navigate'
  | 'modal_open'
  | 'modal_close'
  | 'form_submit'
  | 'feature'
  | 'error_boundary';

export interface Breadcrumb {
  ts: string;          // ISO timestamp
  type: BreadcrumbType;
  label: string;       // human-readable summary
  data?: Record<string, unknown>;
}

const MAX_BREADCRUMBS = 50;
const MAX_LABEL_LENGTH = 80;

let buffer: Breadcrumb[] = [];
let installed = false;

function pushBreadcrumb(entry: Omit<Breadcrumb, 'ts'>) {
  if (buffer.length >= MAX_BREADCRUMBS) buffer.shift();
  buffer.push({ ts: new Date().toISOString(), ...entry });
}

/** Returns the best human-readable label for a clicked element. */
function describeElement(el: Element): string {
  const aria = el.getAttribute('aria-label');
  if (aria) return aria.trim().slice(0, MAX_LABEL_LENGTH);

  // For buttons/links, prefer trimmed textContent
  if (el instanceof HTMLButtonElement || el instanceof HTMLAnchorElement) {
    const txt = (el.textContent ?? '').trim();
    if (txt) return txt.slice(0, MAX_LABEL_LENGTH);
  }

  // Inputs: report their name/role, never their value
  if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
    return `${el.tagName.toLowerCase()}[${el.name || el.id || el.type}]`;
  }

  // Fallback: tag + id/class hint
  const id = el.id ? `#${el.id}` : '';
  const cls = el.className && typeof el.className === 'string'
    ? '.' + el.className.split(/\s+/).slice(0, 2).join('.')
    : '';
  const tag = el.tagName.toLowerCase();
  return `<${tag}${id}${cls}>`.slice(0, MAX_LABEL_LENGTH);
}

export function installBreadcrumbs() {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  // Clicks — walk up to find the nearest interactive element
  document.addEventListener('click', (ev) => {
    let el = ev.target as Element | null;
    let depth = 0;
    while (el && depth < 5) {
      if (
        el instanceof HTMLButtonElement ||
        el instanceof HTMLAnchorElement ||
        el.getAttribute('role') === 'button' ||
        el.getAttribute('role') === 'menuitem' ||
        el.getAttribute('role') === 'tab'
      ) {
        pushBreadcrumb({
          type: 'click',
          label: describeElement(el),
          data: { tag: el.tagName.toLowerCase(), role: el.getAttribute('role') ?? undefined },
        });
        return;
      }
      el = el.parentElement;
      depth++;
    }
  }, { capture: true });

  // Route changes — patch history.pushState/replaceState + listen for popstate
  let lastPath = window.location.pathname + window.location.search;
  const emitNavigate = () => {
    const next = window.location.pathname + window.location.search;
    if (next === lastPath) return;
    pushBreadcrumb({ type: 'navigate', label: `${lastPath} → ${next}` });
    lastPath = next;
  };

  const origPushState = history.pushState.bind(history);
  history.pushState = function (data, unused, url) {
    origPushState(data, unused, url);
    emitNavigate();
  };
  const origReplaceState = history.replaceState.bind(history);
  history.replaceState = function (data, unused, url) {
    origReplaceState(data, unused, url);
    emitNavigate();
  };
  window.addEventListener('popstate', emitNavigate);

  // Form submits — capture form id/name only, never field values
  document.addEventListener('submit', (ev) => {
    const form = ev.target as HTMLFormElement | null;
    if (!form) return;
    pushBreadcrumb({
      type: 'form_submit',
      label: form.id || form.name || form.getAttribute('aria-label') || '<form>',
    });
  }, { capture: true });
}

/** Manually flag a high-value moment — e.g. boq:export, project:duplicate. */
export function addBreadcrumb(label: string, data?: Record<string, unknown>, type: BreadcrumbType = 'feature') {
  pushBreadcrumb({ type, label: label.slice(0, MAX_LABEL_LENGTH), data });
}

export function getBreadcrumbs(): Breadcrumb[] {
  return [...buffer];
}

export function clearBreadcrumbs() {
  buffer = [];
}

/** Pretty-printed text for inclusion in a GitHub issue body. */
export function breadcrumbsAsText(crumbs: Breadcrumb[] = buffer): string {
  return crumbs
    .map((b) => {
      const time = b.ts.slice(11, 23); // HH:mm:ss.SSS
      const payload = b.data && Object.keys(b.data).length > 0 ? ` ${JSON.stringify(b.data)}` : '';
      return `${time}  ${b.type.padEnd(13)} ${b.label}${payload}`;
    })
    .join('\n');
}
